import { FC, useState, useEffect, useRef } from "react"
import styled from "styled-components"
import { ContentfulProduct } from "@lib/types"
import { decode } from "shopify-gid"
import { getOptionValue } from "@lib/utils"
import type { ProductVariant, ProductVariantEdge } from "shopify-storefront-api-typings"

import { useCheckout, useCustomer, useDictionary, useUI, useTracking } from "@hooks/index"
import { SizeChart } from "./index"
import { Button, buttonStyles, Modal } from "@components/ui"

const VariantName = ({ name: n }: { name: string }) => {
  const [name, ...specification] = n.split(" ")

  return (
    <StyledVariantName>
      {name}
      <Specification>{specification.join(" ")}</Specification>
    </StyledVariantName>
  )
}

const StyledVariantName = styled.div`
  letter-spacing: 2px;
`

const Specification = styled.div`
  font-size: 1rem;
  color: ${({ theme }) => theme.color.grey};
`
interface Props {
  selectedVariant: ProductVariant | null
  setSelectedVariant: (v: ProductVariant) => void
  variants: ProductVariantEdge[]
  sizeChart: ContentfulProduct["sizeChart"]
  trackingInfo: {
    name: string
    category: string
    url: string
  }
}

export const SizeSelector: FC<Props> = ({
  selectedVariant,
  setSelectedVariant,
  variants,
  sizeChart,
  trackingInfo,
}) => {
  const dictionary = useDictionary()
  const { openCart, clickAddToBag } = useUI()
  const { id, addLineItem } = useCheckout()
  const { track, tikTokTrack } = useTracking()
  const { currency } = useCustomer()
  const [showOutOfStockMsg, setShowOutOfStockMsg] = useState(false)
  const [openSizeChart, setOpenSizeChart] = useState(false)
  const [loading, setLoading] = useState(false)

  const addToBag = async (variant: ProductVariant) => {
    setLoading(true)
    await addLineItem({ quantity: 1, variantId: variant.id })
    setLoading(false)
    openCart()
    clickAddToBag()
    track("Product Added", {
      ...trackingInfo,
      cart_id: decode(id).id,
      product_id: decode(variant?.id).id,
      variant: variant?.title,
      sku: variant?.sku,
      price: parseFloat(variant?.priceV2?.amount),
      brand: "Haute Hijab",
      quantity: 1,
      image_url: variant?.image?.transformedSrc,
    })
    const currencyAdjustedPrice =
      variant?.presentmentPrices.edges.find(
        (edge: { node: { price: { currencyCode: string } } }) =>
          edge.node.price.currencyCode === currency
      )?.node.price.amount || null
    tikTokTrack("AddToCart", {
      content_type: "product",
      content_id: decode(variant?.id).id,
      content_name: variant?.title,
      currency: currency,
      price: currencyAdjustedPrice,
      value: currencyAdjustedPrice,
      quantity: 1,
    })
  }

  useEffect(() => {
    if (!selectedVariant) return
    if (!selectedVariant.availableForSale) {
      setShowOutOfStockMsg(true)
    } else {
      setShowOutOfStockMsg(false)
    }

    return () => {
      setShowOutOfStockMsg(false)
    }
  }, [selectedVariant])

  const quantityAvailable = selectedVariant && selectedVariant.quantityAvailable
  const showLowStock = quantityAvailable! > 0 && quantityAvailable! < 7

  return (
    <StyledSizeSelector>
      <SizeChartWrapper>
        {variants.length === 1 ? (
          <SizeText>
            {`${dictionary.size}:`} <span>{getOptionValue(variants[0].node.selectedOptions)}</span>
          </SizeText>
        ) : (
          <div>{dictionary.size}</div>
        )}
        {sizeChart && (
          <>
            <SizeChartButton
              type="button"
              onClick={() => {
                setOpenSizeChart(true)
                track("Size Chart Open")
              }}
            >
              {dictionary.sizeChart}
            </SizeChartButton>
            <Modal
              open={openSizeChart}
              onClose={() => {
                setOpenSizeChart(false)
                track("Size Chart Closed")
              }}
            >
              <SizeChart
                title={sizeChart.title}
                subtitle={sizeChart.subtitle}
                table={sizeChart.table}
                image={sizeChart.image}
                richText={sizeChart.richText}
              />
            </Modal>
          </>
        )}
      </SizeChartWrapper>
      {variants?.length > 1 && (
        <Sizes id="size-selector" $slots={variants.length}>
          {variants.map(({ node }) => {
            return (
              <RadioButton
                key={node.id}
                $themeName="ghostBlack"
                $isActive={selectedVariant?.id === node.id}
              >
                <VariantName name={getOptionValue(node.selectedOptions)} />
                <input
                  type="radio"
                  name="size"
                  value={node.id}
                  onChange={() => setSelectedVariant(node)}
                />
              </RadioButton>
            )
          })}
        </Sizes>
      )}
      <AddToBag
        stickToBottom={true}
        buttonTheme="ghostBlackReverse"
        title={!selectedVariant?.availableForSale ? "Select size" : "Add to bag"}
        loading={loading}
        disabled={!selectedVariant?.availableForSale}
        onClick={() => selectedVariant && addToBag(selectedVariant)}
        showOutOfStockMsg={showOutOfStockMsg}
        selectedVariant={selectedVariant}
        showLowStock={showLowStock}
        quantityAvailable={quantityAvailable}
      />
    </StyledSizeSelector>
  )
}

const StyledSizeSelector = styled.div``

const SizeChartWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 0 0 1.4rem;
  text-transform: uppercase;
  font-family: ${({ theme }) => theme.font.heading};
  color: ${({ theme }) => theme.color.greyBlack};
  font-size: 1.2rem;
  font-weight: 300;
  line-height: 2.8rem;
  letter-spacing: 0.12em;
`

const SizeChartButton = styled.button`
  text-transform: inherit;
  font-size: inherit;
  font-family: ${({ theme }) => theme.font.heading};
  line-height: inherit;
  letter-spacing: inherit;
  text-decoration: underline;
`

interface SizesProps {
  $slots: number
}

const SizeText = styled.div`
  span {
    font-weight: 700;
  }
`

const Sizes = styled.div<SizesProps>`
  display: grid;
  grid-template-columns: ${({ $slots }) => `repeat(${$slots <= 3 ? $slots : 3}, 1fr)`};
  grid-gap: 1rem;
  margin: 0 0 2.2rem;
`

interface RadioProps {
  $themeName: string
  $isActive: boolean
}

const RadioButton = styled.label<RadioProps>`
  ${buttonStyles}
  position: relative;
  display: grid;
  place-items: center;

  border-color: ${({ theme, $isActive }) =>
    $isActive ? theme.color.black : theme.color.greyLight};
  background: ${({ theme, $isActive }) => ($isActive ? theme.color.black : theme.color.white)};
  color: ${({ theme, $isActive }) => ($isActive ? theme.color.white : theme.color.black)};

  input {
    opacity: 0;
    position: absolute;
    width: 0;
    height: 0;
  }
`

const AddToBag = styled(Button)`
  width: 100%;
  padding: 1.5rem;
  font-size: 1.3rem;
  letter-spacing: 2px;
`

const LowStock = styled.div`
  margin: 0.7rem 0 0;
  text-align: center;
  font-size: 1.2rem;
  line-height: 2.4rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: ${({ theme }) => theme.color.grey};
  @media (max-width: ${({ theme }: any) => theme.breakpoint.md}px) {
    visibility: hidden;
    pointer-events: none;
  }
  span {
    font-weight: 700;
  }
`
